import { createContext, useContext, useState, useCallback, useMemo, useEffect, ReactNode } from 'react';
import { View, StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ToastComponent, type Toast, type ToastAction, type ToastType } from './Toast';
import { registerToastPresenter } from '@/services/toastBridge';

export type ShowToastOptions = {
  message: string;
  type?: ToastType;
  duration?: number;
  details?: string;
  actions?: ToastAction[];
  onUndo?: () => void;
};

type ToastContextType = {
  showToast: (message: string, type?: ToastType, duration?: number) => void;
  showToastWithOptions: (options: ShowToastOptions) => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback(
    (message: string, type: ToastType = 'info', duration = 3000) => {
      const id = `toast-${Date.now()}-${Math.random()}`;
      const newToast: Toast = { id, message, type, duration };
      setToasts((prev) => [...prev, newToast]);
    },
    []
  );

  const showToastWithOptions = useCallback((options: ShowToastOptions) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    const hasActions = (options.actions?.length ?? 0) > 0 || !!options.onUndo;
    const duration = options.duration ?? (hasActions ? 6000 : 3000);
    const newToast: Toast = {
      id,
      message: options.message,
      type: options.type ?? 'info',
      duration,
      details: options.details,
      actions: options.actions,
      onUndo: options.onUndo,
    };
    setToasts((prev) => [...prev, newToast]);
  }, []);

  const showSuccess = useCallback(
    (message: string, duration = 3000) => showToast(message, 'success', duration),
    [showToast]
  );

  const showError = useCallback(
    (message: string, duration = 4000) => showToast(message, 'error', duration),
    [showToast]
  );

  const showInfo = useCallback(
    (message: string, duration = 3000) => showToast(message, 'info', duration),
    [showToast]
  );

  const showWarning = useCallback(
    (message: string, duration = 3500) => showToast(message, 'warning', duration),
    [showToast]
  );

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const contextValue = useMemo(
    () => ({
      showToast,
      showToastWithOptions,
      showSuccess,
      showError,
      showInfo,
      showWarning,
    }),
    [showToast, showToastWithOptions, showSuccess, showError, showInfo, showWarning],
  );

  useEffect(() => {
    return registerToastPresenter((payload) => {
      showToastWithOptions({
        message: payload.message,
        type: payload.type,
        duration: payload.duration,
        details: payload.details,
      });
    });
  }, [showToastWithOptions]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <SafeAreaView style={styles.container} edges={['top']} pointerEvents="box-none">
        <View style={styles.toastContainer}>
          {toasts.map((toast) => (
            <ToastComponent key={toast.id} toast={toast} onDismiss={dismissToast} />
          ))}
        </View>
      </SafeAreaView>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    pointerEvents: 'box-none',
  },
  toastContainer: {
    paddingHorizontal: 16,
    paddingTop: 8,
  },
});

